import React from 'react';
import {useMutation, useQuery, useQueryClient, type QueryKey} from '@tanstack/react-query';
import {useFormContext} from 'react-hook-form';
import {useProjectReadOnly} from './projectForm.helpers';
import {useSectionSaveCoordinator} from './SectionSaveCoordinator';
import { useTranslation } from 'react-i18next';

type WithOptionalId = { id?: number | null };

export function toSingleton<T>(value: T | T[] | null | undefined): T | null {
    if (Array.isArray(value)) {
        return value[0] ?? null;
    }

    return value ?? null;
}

export function SectionAlerts(props: {
    isLoading?: boolean;
    loadError?: unknown;
    loadingText?: string;
}) {
    const { t } = useTranslation();
    return (
        <>
            {props.isLoading ? <div className="muted">{props.loadingText ?? t('sectionCrudUtils.loading')}</div> : null}
            {props.loadError ? (
                <div className="alert alertError">
                    <strong>{t('sectionCrudUtils.errorLoading')}</strong>
                    <div className="muted">{renderSectionError(props.loadError)}</div>
                </div>
            ) : null}
        </>
    );
}

export function SectionSaveButton(props: {
    onClick: () => void;
    busy?: boolean;
    label?: string;
}) {
    const { t } = useTranslation();
    const readOnly = useProjectReadOnly();
    const form = useFormContext();
    const coordinator = useSectionSaveCoordinator();

    if (readOnly || coordinator) {
        return null;
    }

    return (
        <div className="inlineActions">
            <button
                className="btn btnPrimary"
                type="button"
                onClick={props.onClick}
                disabled={props.busy || !form.formState.isDirty}
            >
                {props.busy ? t('sectionCrudUtils.saving') : props.label ?? t('sectionCrudUtils.saveSection')}
            </button>
        </div>
    );
}

export function useCollectionSection<TItem extends WithOptionalId>(params: {
    queryKey: QueryKey;
    proyectoId: number;
    getFn: (proyectoId: number) => Promise<TItem[]>;
    createFn: (proyectoId: number, data: TItem) => Promise<unknown>;
    updateFn: (proyectoId: number, id: number, data: TItem) => Promise<unknown>;
    deleteFn: (proyectoId: number, id: number) => Promise<unknown>;
    enabled?: boolean;
}) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: [...params.queryKey, params.proyectoId],
        queryFn: () => params.getFn(params.proyectoId),
        enabled: (params.enabled ?? true) && Number.isFinite(params.proyectoId) && params.proyectoId > 0,
    });

    const mutation = useMutation({
        mutationFn: (items: TItem[]) =>
            syncCollection({
                initialItems: query.data ?? [],
                currentItems: items,
                createFn: (item) => params.createFn(params.proyectoId, item),
                updateFn: (itemId, item) => params.updateFn(params.proyectoId, itemId, item),
                deleteFn: (itemId) => params.deleteFn(params.proyectoId, itemId),
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: [...params.queryKey, params.proyectoId], exact: true});
        },
    });

    return {
        ...query,
        save: mutation.mutateAsync,
        saveMutation: mutation,
    };
}

export async function syncCollection<TItem extends WithOptionalId>(params: {
    initialItems: TItem[];
    currentItems: TItem[];
    createFn: (item: TItem) => Promise<unknown>;
    updateFn: (id: number, item: TItem) => Promise<unknown>;
    deleteFn: (id: number) => Promise<unknown>;
}) {
    const initialById = new Map(
        params.initialItems
            .filter((item) => typeof item.id === 'number' && item.id > 0)
            .map((item) => [item.id as number, item])
    );

    const currentById = new Map(
        params.currentItems
            .filter((item) => typeof item.id === 'number' && item.id > 0)
            .map((item) => [item.id as number, item])
    );

    for (const item of params.currentItems) {
        if (typeof item.id === 'number' && item.id > 0) {
            await params.updateFn(item.id, item);
            continue;
        }

        await params.createFn(stripEmptyId(item));
    }

    const deletedIds: number[] = [];
    initialById.forEach((_, id) => {
        if (!currentById.has(id)) {
            deletedIds.push(id);
        }
    });

    for (const id of deletedIds) {
        await params.deleteFn(id);
    }
}

function stripEmptyId<TItem extends WithOptionalId>(item: TItem): TItem {
    if (typeof item.id === 'number' && item.id > 0) {
        return item;
    }

    const {id, ...rest} = item as TItem & { id?: number | null };
    return rest as TItem;
}

function renderSectionError(error: unknown) {
    if (error instanceof Error) {
        return error.message;
    }

    return String(error);
}
